import type { AllChartsDataMap } from '../modules/analytic/types';
import type { IPAColumnTypes } from '../types/page-analytic-v2';
import type { RawMetricRow } from '../helpers/pageDetailV2';
import type { ETimeDimension } from '~/core/gemxql/types';

import { usePageAnalyticFilterStore } from '../stores/page-analytic-filter';
import { usePAAnalyticLineChart } from '../hooks/usePAAnalyticLineChart';
import { LOCATION_PATH_KEY, SHOPIFY_PAGE_ID_KEY } from '../constants';
import { useFetchPageDetails } from '../hooks/useFetchPageDetails';
import { useDateTimeFilter } from '../hooks/useDateTimeFilter';
import { CompareDateTimePickerAlias } from '~/presentation/ui';
import { isRawMetricRowEmpty } from '../helpers/pageDetailV2';
import { createContext, useContext, useEffect } from 'react';
import { useMetricsTimeFilter } from '~/modules/analytics';
import { usePageAnalyticDetailsStore } from '../stores';
import { useFeatureFlagChecker } from '~/modules/apps';
import { useSearchParams } from '@remix-run/react';

export interface PageAnalyticDetailContextType {
  totalsRow?: RawMetricRow;
  comparisonTotalsRow?: RawMetricRow;
  columnTypes?: IPAColumnTypes;
  isLoading: boolean;
  hideComparison: boolean;
  isEmptyMetricData: boolean;
  interval?: ETimeDimension;
  currentPeriodChartName: string;
  previousPeriodChartName: string;
  allChartsDataMap?: AllChartsDataMap;
}

export interface PageAnalyticDetailProviderProps {
  children: React.ReactNode;
}

export const PageAnalyticDetailContext = createContext<PageAnalyticDetailContextType | undefined>(
  undefined,
);

export const PageAnalyticDetailProvider = ({ children }: PageAnalyticDetailProviderProps) => {
  useDateTimeFilter();

  const analyticMode = usePageAnalyticFilterStore((state) => state.analyticMode);
  const setPageTitle = usePageAnalyticDetailsStore((state) => state.setPageTitle);
  const { isEnabledFilterByShopifyPageId } = useFeatureFlagChecker();

  const { currentDates, compareCurrentDates: previousDates } = useMetricsTimeFilter();
  const [searchParams] = useSearchParams();

  const shopifyPageId = searchParams.get(SHOPIFY_PAGE_ID_KEY) || '';
  const locationPath = searchParams.get(LOCATION_PATH_KEY) || '';
  const isQueryPreviousData = previousDates.alias !== CompareDateTimePickerAlias.NO_COMPARISON;

  const {
    totalsRow,
    comparisonTotalsRow,
    allChartsDataMap,
    columnTypes,
    pageItems,
    interval,
    isFetching,
  } = useFetchPageDetails({
    mode: analyticMode,
    currentDates,
    previousDates,
    isQueryPreviousData,
    locationPath,
    shopifyPageId,
    isEnabledFilterByShopifyPageId,
  });

  useEffect(() => {
    if (pageItems?.page_title) {
      setPageTitle(pageItems.page_title);
    }
  }, [pageItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const isEmptyCurrentData = isRawMetricRowEmpty(totalsRow);
  const isEmptyPreviousData = !isQueryPreviousData || isRawMetricRowEmpty(comparisonTotalsRow);
  const isEmptyMetricData = isEmptyCurrentData && isEmptyPreviousData;

  const { currentPeriodChartName, previousPeriodChartName } = usePAAnalyticLineChart({
    currentDates,
    previousDates,
  });

  const contextValue: PageAnalyticDetailContextType = {
    totalsRow,
    comparisonTotalsRow,
    columnTypes,
    isLoading: isFetching,
    hideComparison: !isQueryPreviousData,
    isEmptyMetricData,
    interval,
    currentPeriodChartName,
    previousPeriodChartName,
    allChartsDataMap,
  };

  return (
    <PageAnalyticDetailContext.Provider value={contextValue}>
      {children}
    </PageAnalyticDetailContext.Provider>
  );
};

export const usePageAnalyticDetailContext = () => {
  const context = useContext(PageAnalyticDetailContext);
  if (!context) {
    throw new Error(
      'usePageAnalyticDetailContext must be used within a PageAnalyticDetailProvider',
    );
  }
  return context;
};
